import { Link } from "react-router-dom";
import { GraduationCap, ArrowLeft } from "@phosphor-icons/react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#F7F3EE]">
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl gradient-primary flex items-center justify-center">
              <GraduationCap size={18} color="white" weight="duotone" />
            </div>
            <div>
              <div className="font-heading font-black text-base leading-none">Caminhos</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-[#1E5FFF] font-semibold flex items-center gap-1 hover:underline">
            <ArrowLeft size={14} /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-16 space-y-6">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">Contrato de uso</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-[#12143A] tracking-tight">Termos de Uso</h1>
          <p className="text-sm text-zinc-500 mt-3">Última atualização: 5 de agosto de 2026 • Vigência imediata</p>
        </div>

        <div className="card-soft p-6 sm:p-8 space-y-6 text-[15px] leading-relaxed text-zinc-700">
          <Section title="1. Aceitação dos termos">
            <p>
              Ao criar uma conta e utilizar a plataforma <b>Caminhos APP</b>, você declara ter lido, compreendido e concordado
              integralmente com estes Termos de Uso e com nossa <Link to="/privacy" className="text-[#1E5FFF] underline">Política de Privacidade</Link>.
              Se não concordar, não utilize a plataforma.
            </p>
          </Section>

          <Section title="2. Descrição do serviço">
            <p>
              A Caminhos APP é uma plataforma digital de <b>acompanhamento de estudos</b> destinada a alunos em preparação
              para vestibulares e concursos. Oferece agenda, controle de desempenho, materiais didáticos, sessões
              1:1 com professores (via Google Meet), Tutor IA e painel para responsáveis.
            </p>
          </Section>

          <Section title="3. Cadastro e conta">
            <ul className="list-disc pl-5 space-y-2">
              <li>É necessário ter pelo menos <b>13 anos</b>. Menores devem ter autorização dos pais/responsáveis.</li>
              <li>Informações fornecidas devem ser verdadeiras, precisas e atualizadas.</li>
              <li>Você é responsável por manter a confidencialidade das suas credenciais.</li>
              <li>Uma conta é pessoal e intransferível. É proibido compartilhar login com terceiros.</li>
            </ul>
          </Section>

          <Section title="4. Mensalidades e acesso">
            <p>
              O acesso do aluno depende da <b>adimplência das mensalidades</b>. O administrador registra os pagamentos manualmente.
              Contas com pagamentos vencidos podem ter o acesso bloqueado até a regularização. O responsável pode
              acompanhar o histórico financeiro pelo painel.
            </p>
          </Section>

          <Section title="5. Uso permitido">
            <p>Você concorda em utilizar a plataforma apenas para fins educacionais legítimos. É <b>proibido</b>:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Utilizar a plataforma para atividades ilegais, ofensivas ou que violem direitos de terceiros;</li>
              <li>Compartilhar, revender ou disponibilizar publicamente materiais didáticos ou gravações;</li>
              <li>Fazer engenharia reversa, scraping ou tentar burlar controles de segurança;</li>
              <li>Enviar spam, malware, conteúdo discriminatório ou sexual pelo Tutor IA / chat;</li>
              <li>Criar múltiplas contas para contornar bloqueios de inadimplência.</li>
            </ul>
            <p>O descumprimento pode resultar em suspensão imediata da conta.</p>
          </Section>

          <Section title="6. Propriedade intelectual">
            <p>
              Todo o conteúdo da plataforma (código, design, textos, materiais didáticos, o livro <em>Os Caminhos para Aprovação</em>,
              logos e marcas) é propriedade da Caminhos APP ou de seus licenciantes e protegido pela Lei 9.610/1998
              (Direitos Autorais). É concedida a você apenas licença limitada, pessoal, não-exclusiva e revogável de uso.
            </p>
          </Section>

          <Section title="7. Conteúdo gerado pelo usuário">
            <p>
              Ao enviar anotações, redações, dúvidas ou qualquer conteúdo à plataforma, você garante possuir os direitos
              necessários e concede à Caminhos APP licença limitada para armazenar, exibir e processar esse conteúdo com o
              único objetivo de prestar o serviço educacional. Você continua sendo o titular do conteúdo.
            </p>
          </Section>

          <Section title="8. Tutor IA e limitações">
            <p>
              O Tutor IA é uma ferramenta de apoio pedagógico baseada em modelos de linguagem (OpenAI e Google Gemini).
              As respostas <b>podem conter imprecisões</b> e não substituem a orientação de um professor humano. Não use
              respostas do Tutor IA como fonte única para decisões críticas.
            </p>
          </Section>

          <Section title="9. Suspensão e encerramento">
            <p>
              Podemos suspender ou encerrar sua conta em caso de: violação destes Termos, inadimplência prolongada,
              uso fraudulento ou por ordem judicial. Você pode encerrar sua conta a qualquer momento pelo painel{" "}
              <Link to="/data-rights" className="text-[#1E5FFF] underline">Meus Dados</Link>.
            </p>
          </Section>

          <Section title="10. Limitação de responsabilidade">
            <p>
              A Caminhos APP não garante aprovação em nenhum vestibular ou concurso — o serviço oferece apoio pedagógico,
              e o resultado depende do esforço e dedicação do estudante. Não nos responsabilizamos por indisponibilidades
              temporárias, perda de dados de terceiros (Google, provedores de IA) ou danos indiretos.
            </p>
          </Section>

          <Section title="11. Alterações">
            <p>
              Podemos atualizar estes Termos a qualquer momento. Alterações relevantes serão comunicadas por email ou
              aviso na plataforma. O uso continuado após as alterações significa aceitação dos novos termos.
            </p>
          </Section>

          <Section title="12. Foro">
            <p>
              Estes Termos são regidos pelas leis da <b>República Federativa do Brasil</b>. Fica eleito o foro da comarca
              onde o usuário reside para dirimir quaisquer controvérsias, na forma do Art. 101 do CDC (Lei 8.078/1990),
              quando aplicável.
            </p>
          </Section>

          <Section title="13. Contato">
            <p>Dúvidas sobre estes Termos: <a href="mailto:midiatecnointerativa@gmail.com" className="text-[#1E5FFF] underline">midiatecnointerativa@gmail.com</a></p>
          </Section>
        </div>

        <div className="text-center text-xs text-zinc-500 py-8">
          Caminhos APP • <Link to="/privacy" className="text-[#1E5FFF] underline">Política de Privacidade</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-2xl font-black text-[#12143A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
